import SubjectSymbol from '@/components/SubjectSymbol';
import CultivarNames from "@/components/CultivarNames";
import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Sprout, Ruler } from "lucide-react";
import { OpportunityBadge } from "@/components/OpportunityBadge";
import { subjectLabel } from "@/lib/subjects";
import { useLang } from "@/i18n/LanguageContext";

export const formatLocation = (loc = {}, tv = (x) => x) =>
  [loc.locality, loc.municipality !== loc.locality ? loc.municipality : null, tv(loc.region), tv(loc.country)].filter(Boolean).join(", ");

export const FarmCard = ({ farm, showCultivars = false, compact = false }) => {
  const { t, tv, lang, loc: localize } = useLang();
  const loc = farm.location || {};
  const area = farm.quick_facts?.area_ha;
  const opps = [...new Set((farm.opportunities || []).filter((o) => o.active).map((o) => o.type))];

  return (
    <Link
      to={farm.public_path || `/farm/${farm.id}`}
      data-testid={`farm-card-${farm.id}`}
      className={`group flex ${compact ? "flex-row" : "flex-col"} h-full bg-[#FFFDF8] rounded-md border border-[#E2DACB] overflow-hidden hover:-translate-y-1 hover:shadow-[0_14px_34px_rgba(85,116,72,0.14)] transition-[transform,box-shadow] duration-300 ease-out`}
    >
      <div className={compact ? "relative w-24 sm:w-32 shrink-0 overflow-hidden" : "relative h-44 shrink-0 overflow-hidden"}>
        {(farm.cover_image || farm.image) ? <img
          src={farm.cover_image || farm.image}
          alt={farm.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
        /> : <div className="h-full bg-[#EFE9DA] flex items-center justify-center"><SubjectSymbol type={farm.subject_type} size={compact?48:72} /></div>}
        {farm.profile_image && <img src={farm.profile_image} alt="" loading="lazy" className={`absolute bottom-2 left-2 ${compact?"w-10 h-10":"w-14 h-14"} object-contain p-1 bg-[#FFFDF8] rounded border border-[#E2DACB]`}/>}
        {farm.is_demo && (
          <span className="absolute top-3 left-3 bg-[#2A2521]/75 text-[#F8F5EA] text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full backdrop-blur-sm">
            {t("farm.demoFarm")}
          </span>
        )}
      </div>
      <div className={`${compact?"p-3":"p-5"} min-w-0 flex-1`}>

        <p className="text-sm text-[#557448] mb-2 flex items-center gap-2"><SubjectSymbol type={farm.subject_type} size={24}/>{subjectLabel(farm.subject_type, lang)}</p>
        <h3 className="font-serif text-xl font-bold text-[#2A2521] leading-tight line-clamp-2 group-hover:text-[#557448] transition-colors">
          {farm.name}
        </h3>
        <p className="flex items-center gap-1.5 text-[#6E6258] text-sm mt-1.5">
          <MapPin size={14} className="text-[#C46A3A] shrink-0" />
          {formatLocation(loc, tv)}
        </p>
        <div className="flex items-center gap-4 mt-3 text-sm text-[#4A443D]">
          {(farm.quick_facts?.crop_type || farm.main_crop) && <span className="flex items-center gap-1.5">
            <Sprout size={15} className="text-[#557448]" />
            {tv(farm.quick_facts?.crop_type) || tv(farm.main_crop)}
          </span>}
          {area != null && (
            <span className="flex items-center gap-1.5">
              <Ruler size={15} className="text-[#8FA878]" />~{area} ha
            </span>
          )}
        </div>
        {!compact && localize(farm.description) && <p className="pa-home-description text-sm text-[#6E6258] line-clamp-3 mt-3">{localize(farm.description)}</p>}
        {showCultivars && !compact && <CultivarNames farm={farm} />}
        {!compact && opps.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-4">
            {opps.map((o) => (
              <OpportunityBadge key={o} type={o} />
            ))}
          </div>
        )}
      </div>
    </Link>
  );
};

